import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Input } from '@components/Input';
import ComboLocationMultiselect, { LOCATION_TYPE } from '@features/ComboLocationMultiselect';
import { useTranslation, useYupValidationResolver, useLocations } from '@hooks';
import { knowledgeBaseService } from '@services';
import { alertActions, knowledgeBaseActions } from '@actions';
import { handleApiError } from '@helpers/api';
import { Modal, Content } from './styles';

export const FORM_TYPE = {
    NEW: 'NEW',
    EDIT: 'EDIT',
    EDIT_COPY: 'EDIT_COPY',
    DELETE: 'DELETE',
    VIEW: 'VIEW',
};

const EditModal = ({
    selectedContent,
    setSelectedContent,
    currentQuery,
    brands,
    modal,
    setModal,
    page,
    setPage,
    rowsPerPage,
    setDeleteModalOpen,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isMobile = useMediaQuery('@media (max-width: 768px)');
    const { getLocationsOptions } = useLocations();

    const KnowledgeBaseSchema = yup.object().shape({
        title: yup.string().required('validation.required'),
        locations: yup.array().when(['locationType', 'brandAccess'], {
            is: (value, brandAccess) =>
                value === LOCATION_TYPE.LOCATIONS || (value === LOCATION_TYPE.BRANDS && !brandAccess),
            then: yup.array().min(1, 'validation.required'),
            otherwise: yup.array(),
        }),
        brands: yup.array().when(['locationType', 'brandAccess'], {
            is: (value, brandAccess) => value === LOCATION_TYPE.BRANDS && brandAccess,
            then: yup.array().min(1, 'validation.required'),
            otherwise: yup.array(),
        }),
        question: yup.string().required('validation.required'),
        answer: yup.string().required('validation.required').max(2000, 'validation.maxLength'),
        locationType: yup.string(),
    });

    const resolver = useYupValidationResolver(KnowledgeBaseSchema);

    const {
        getValues,
        setValue,
        control,
        watch,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: {
            title: '',
            locations: [],
            brands: [],
            question: '',
            answer: '',
            locationType: LOCATION_TYPE.BRANDS,
        },
    });

    const locationsOptions = useMemo(() => getLocationsOptions(), []);

    const brandsOptions = brands.map(brand => ({ value: brand.id, label: brand.title }));

    useEffect(() => {
        if (modal.type !== FORM_TYPE.NEW) {
            setValue('title', selectedContent.title, { shouldValidate: true });
            setValue('question', selectedContent.question, { shouldValidate: true });
            setValue('answer', selectedContent.answer, { shouldValidate: true });
        }
    }, [selectedContent, modal.type, setValue]);

    const onClose = () => {
        setSelectedContent(null);
        setModal({ open: false, type: FORM_TYPE.NEW });
    };

    const refetchFirstPage = async () => {
        if (page === 0) {
            await dispatch(knowledgeBaseActions.get(currentQuery, page, rowsPerPage));
        } else {
            setPage(0); // INFO: Triggers refetch of templates
        }
    };

    const REFETCH_MAP = {
        [FORM_TYPE.EDIT]: async () => await dispatch(knowledgeBaseActions.get(currentQuery, page, rowsPerPage)),
        [FORM_TYPE.NEW]: () => refetchFirstPage(),
        [FORM_TYPE.EDIT_COPY]: () => refetchFirstPage(),
    };

    const RESPONSE_MSG = {
        [FORM_TYPE.EDIT]: {
            success: t('alertMessages.editSuccess'),
            error: t('alertMessages.editFail'),
        },
        [FORM_TYPE.NEW]: {
            success: t('alertMessages.createSuccess'),
            error: t('alertMessages.createFail'),
        },
        [FORM_TYPE.EDIT_COPY]: {
            success: t('alertMessages.createSuccess'),
            error: t('alertMessages.createFail'),
        },
    };

    const onSubmit = async () => {
        const { locations, ...values } = getValues();
        try {
            await knowledgeBaseService.addOrUpdateKnowledgeBase({
                ...values,
                locations: locations.map(l => l.id),
                type: 1, // INFO: 1 - QA
                ...(modal.type === FORM_TYPE.EDIT && { id: selectedContent.id }),
            });
            dispatch(alertActions.success(RESPONSE_MSG[modal.type].success));
            REFETCH_MAP[modal.type]();
            setModal({ open: false, type: FORM_TYPE.NEW });
            setSelectedContent(null);
        } catch (error) {
            handleApiError(error, RESPONSE_MSG[modal.type].error);
        }
    };

    const isView = modal.type === FORM_TYPE.VIEW;

    return (
        <Modal
            isOpen={modal.open}
            handleClose={onClose}
            title={
                modal.type === FORM_TYPE.NEW || modal.type === FORM_TYPE.EDIT_COPY
                    ? t('buttons.addContent')
                    : t('KnowledgeBase.editContent')
            }
            onPrimaryAction={handleSubmit(onSubmit)}
            primaryActionText={t('buttons.save')}
            primaryActionDisabled={isSubmitting || !isValid || !isDirty}
            onSecondaryAction={onClose}
            secondaryActionText={t('buttons.cancel')}
            onAdditionalAction={selectedContent?.id ? () => setDeleteModalOpen(true) : null}
            additionalTextButton={t('buttons.delete')}
            additionalActionDisabled={isSubmitting}
        >
            <Content>
                <Controller
                    control={control}
                    name="title"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('common.title')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            required
                            sx={{ gridColumn: isMobile ? '1 / span 1' : '1 / span 2' }}
                            disabled={isView}
                        />
                    )}
                />
                <FormProvider {...{ control, setValue, watch }}>
                    <ComboLocationMultiselect
                        brandsOptions={brandsOptions}
                        locationsOptions={locationsOptions}
                        disabled={isView}
                        data={selectedContent}
                        isView={isView}
                    />
                </FormProvider>
                <Controller
                    control={control}
                    name="question"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('common.question')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            required
                            sx={{ gridColumn: isMobile ? '1 / span 1' : '1 / span 2' }}
                            disabled={isView}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="answer"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('common.answer')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message, { count: 2000 }) : null}
                            multiline
                            required
                            minRows={4}
                            maxLength={2000}
                            sx={{ gridColumn: isMobile ? '1 / span 1' : '1 / span 2' }}
                            disabled={isView}
                        />
                    )}
                />
            </Content>
        </Modal>
    );
};

export default EditModal;
